import {isFeatureEnabled} from 'feature-flags';
import {Action, Actions, Alert} from 'platform/components';
import {Box, HStack, Link, Show, Space, Stack, Text, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface OfferValidity {
  openDialog: () => void;
  validTo?: string;
  isExpired?: boolean;
}

interface BusinessCaseMainProps extends TestIdProps {
  actions?: Action[];
  children?: ReactNode;
  offerValidity?: OfferValidity;
}

export function BusinessCaseMain(props: BusinessCaseMainProps) {
  const showOfferValidity =
    Boolean(props.offerValidity) && isFeatureEnabled(featureFlags.OFFER_VALIDITY);
  const hasHeader = Boolean(props.actions?.length) || showOfferValidity;

  const [canSetOfferValidity] = usePermissions({
    permissionKeys: ['setOfferValidity'],
  });

  return (
    <Box
      flex={1}
      padding={[2, 4]}
      minWidth={0}
      role="main"
      data-testid={suffixTestId('main-box', props)}
    >
      <VStack align="stretch" justify="stretch">
        <VStack align="stretch" justify="stretch">
          <Show when={hasHeader}>
            <Stack direction={['column', 'column', 'row']} spacing={4} justify="space-between">
              <Show when={showOfferValidity}>
                <HStack spacing={2} align="center">
                  <Show when={!props.offerValidity?.isExpired}>
                    <Text size="small" color="secondary">
                      {i18n.t('page.businessCase.labels.offerValidUntil')}:
                    </Text>
                  </Show>

                  <HStack spacing={2} align="center">
                    <Show when={props.offerValidity?.isExpired}>
                      <Alert
                        type="inline"
                        variant="error"
                        title={i18n.t('page.businessCase.labels.offerExpired', {
                          date: props.offerValidity?.validTo,
                        })}
                      />
                    </Show>

                    <Show when={!props.offerValidity?.isExpired}>
                      <Text size="small" color="tertiary">
                        {props.offerValidity?.validTo ?? i18n.t('general.labels.notSet')}
                      </Text>
                    </Show>

                    <Link
                      size="small"
                      leftIcon="image/edit"
                      title={i18n.t('general.actions.edit')}
                      onClick={props.offerValidity?.openDialog}
                      isDisabled={!canSetOfferValidity}
                    />
                  </HStack>
                </HStack>
              </Show>
              <Show onNotebook onDesktop>
                <Space fillAvailable />
              </Show>
              <Actions actions={props.actions} data-testid={suffixTestId('actions', props)} />
            </Stack>

            <Space vertical={4} />
          </Show>

          <Box flex={1} data-testid={suffixTestId('main-content', props)}>
            {props.children}
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
}
